import { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Header } from "./components/Header";
import { Sidebar, type PageId } from "./components/Sidebar";
import { Dashboard } from "./pages/Dashboard";
import { DriveParameters } from "./pages/DriveParameters";
import { Pumps } from "./pages/Pumps";
import { Lubrication } from "./pages/Lubrication";
import { JackingForce } from "./pages/JackingForce";
import { AdminAnalytics } from "./pages/AdminAnalytics";
import { PreprocessingDrawer } from "./components/PreprocessingDrawer";
import { PipeScheduleDrawer } from "./components/Develop/PipeScheduleDrawer";
import { DownloadDrawer, toLocalInputInTz } from "./components/DownloadDrawer";
import { ResyncDrawer } from "./components/ResyncDrawer";
import { DiaryDrawer } from "./components/DiaryDrawer";
import { AlertsDrawer } from "./components/AlertsDrawer";
import { AuthGate, isSlurryPath } from "./components/AuthGate";
import { SlurryManualReadings } from "./components/SlurryManualReadings";
import { api } from "./lib/api";
import { useIsMobileLayout } from "./lib/useMediaQuery";
import { useHydrateSavedPreprocessing, usePreprocessingParams } from "./state/preprocessing";
import { localNaiveInputToUtcIso, useTimezone } from "./lib/datetime";
import { SLURRY_CHANNELS, type DriveInfo, type HealthResponse, type SlurryChannelId } from "@tbm/shared";
import { MobileApp } from "./mobile/MobileApp";
import { startAnalytics, trackFeature, trackPageView } from "./lib/analytics";

export function App() {
  return (
    <AuthGate>
      <RoleSwitch />
    </AuthGate>
  );
}

/**
 * A SLURRY_PASSWORD login only ever gets the slurry view (the backend
 * refuses its other API calls anyway). Full logins see it at /slurry.
 */
function RoleSwitch() {
  const whoami = useQuery({ queryKey: ["whoami"], queryFn: api.whoami, staleTime: 60_000 });
  if (whoami.data?.role === "slurry" || isSlurryPath()) return <SlurryApp />;
  return <AuthenticatedApp />;
}

function SlurryApp() {
  const qc = useQueryClient();
  const preprocessingReady = useHydrateSavedPreprocessing();
  useEffect(() => {
    startAnalytics();
    trackPageView("slurry");
  }, []);
  const logout = async () => {
    await api.logout().catch(() => {});
    await qc.invalidateQueries({ queryKey: ["whoami"] });
  };
  return (
    <SyncGate>
      <div className="flex flex-col min-h-screen bg-bg text-text">
        <header className="flex items-center justify-between h-16 px-6 max-md:px-3 border-b border-border1 bg-surface1">
          <div className="flex items-center gap-4">
            <img src="/logo.png" alt="Ward & Burke" className="h-10 max-md:h-7" />
            <div>
              <div className="text-[10.5px] uppercase tracking-[0.22em] text-dim">
                TBM data portal
              </div>
              <div className="text-[15px] font-semibold text-text">Slurry monitoring</div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => void logout()}
            className="text-[11px] uppercase tracking-wider text-muted hover:text-text"
          >
            Sign out
          </button>
        </header>
        <main className="flex-1 bg-grid">
          <div className="max-w-[1500px] mx-auto px-8 py-7 max-md:px-3 max-md:py-4">
            {preprocessingReady ? (
              <div className="flex flex-col gap-5">
                <DriveParameters variant="slurry" />
                <SlurryManualReadings />
                <SlurryDownload />
              </div>
            ) : (
              <div className="text-[12px] text-muted">Loading…</div>
            )}
          </div>
        </main>
      </div>
    </SyncGate>
  );
}

function AuthenticatedApp() {
  // Seed the globally-saved preprocessing settings before either tree
  // renders, so desktop and mobile share one hydration point.
  const preprocessingReady = useHydrateSavedPreprocessing();
  // Install the analytics flush timer + page-hide hooks once, behind
  // the auth gate so we only collect from logged-in operators.
  useEffect(() => {
    startAnalytics();
  }, []);
  // Every hook must run before any early return, or the hook order
  // changes between renders. `window.location.pathname` can't change
  // without a reload today, so the previous early return happened to be
  // safe — but it made adding a hook above it a latent crash.
  const isMobileLayout = useIsMobileLayout();

  // Hidden admin dashboard at /admin — no sidebar entry. Path-based so
  // it's bookmarkable without polluting the sidebar nav for the rest
  // of the team.
  if (typeof window !== "undefined" && window.location.pathname === "/admin") {
    return <AdminAnalytics />;
  }

  // Hold the first paint until the operator's saved preprocessing has
  // been applied to the store. Without this the pages mounted with
  // DEFAULT_PREPROCESSING, fired the heaviest queries against it, then
  // hydration changed the query key and fired every one of them a
  // second time — and the operator briefly saw data processed with
  // settings that were not theirs.
  if (!preprocessingReady) {
    return (
      <div className="flex items-center justify-center h-screen text-[12px] text-muted">
        Loading…
      </div>
    );
  }

  // Below 1024 px (phones + iPad portrait) hand off to a parallel mobile
  // tree. Above the breakpoint DesktopApp renders the original UI byte
  // for byte. Split into two components so each side has a stable hook
  // order — a single useState in either tree won't shift positions when
  // the viewport flips across the breakpoint.
  return <SyncGate>{isMobileLayout ? <MobileApp /> : <DesktopApp />}</SyncGate>;
}

/**
 * Holds the portal back while a Wipe & resync is re-pulling data, so
 * pages never render a half-loaded drive. The fetcher reports how far
 * it has got (/api/health `sync`); once it has scanned to the end (now,
 * or the drive end date) every query is refetched and the portal opens.
 */
function SyncGate({ children }: { children: React.ReactNode }) {
  const qc = useQueryClient();
  const health = useQuery({
    queryKey: ["health"],
    queryFn: api.health,
    refetchInterval: (q) => (q.state.data?.sync?.caughtUp === false ? 3_000 : 10_000),
  });
  const drive = useQuery({ queryKey: ["drive"], queryFn: api.drive });
  const loading = health.data?.sync?.caughtUp === false;
  const wasLoading = useRef(false);
  useEffect(() => {
    if (wasLoading.current && !loading) void qc.invalidateQueries();
    wasLoading.current = loading;
  }, [loading, qc]);

  // First paint waits for the status (it's a single-row read) so a
  // reload mid-resync doesn't flash the half-loaded portal. A failed
  // status call falls through to the portal rather than blocking it.
  if (health.isLoading) {
    return (
      <div className="flex items-center justify-center h-screen text-[12px] text-muted">
        Loading…
      </div>
    );
  }
  if (!loading || !health.data) return <>{children}</>;
  return <SyncScreen health={health.data} drive={drive.data ?? null} />;
}

function SyncScreen({ health, drive }: { health: HealthResponse; drive: DriveInfo | null }) {
  const tz = drive?.shiftTimezone ?? "Europe/Dublin";
  const startMs = drive ? Date.parse(drive.startDate) : null;
  const syncedMs = health.sync.syncedTo ? Date.parse(health.sync.syncedTo) : null;
  const targetMs = health.sync.target ? Date.parse(health.sync.target) : Date.now();
  const frac =
    startMs != null && syncedMs != null && targetMs > startMs
      ? Math.min(1, Math.max(0, (syncedMs - startMs) / (targetMs - startMs)))
      : 0;
  const fmt = (ms: number) =>
    new Date(ms).toLocaleString("en-GB", {
      timeZone: tz,
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  return (
    <div className="flex items-center justify-center min-h-screen bg-bg text-text px-4">
      <div className="card w-full max-w-[520px] px-6 py-6 space-y-4">
        <img src="/logo.png" alt="Ward & Burke" className="h-10" />
        <div>
          <div className="text-[10.5px] uppercase tracking-[0.22em] text-dim">
            {drive?.name ?? "Drive"}
          </div>
          <h1 className="text-[15px] font-semibold text-text">Loading drive data</h1>
        </div>
        <div className="h-2 rounded bg-surface2 border border-border1 overflow-hidden">
          <div
            className="h-full bg-wb transition-[width] duration-500"
            style={{ width: `${(frac * 100).toFixed(1)}%` }}
          />
        </div>
        <div className="flex justify-between text-[11.5px] text-muted mono-nums">
          <span>{syncedMs != null ? `Loaded to ${fmt(syncedMs)}` : "Starting…"}</span>
          <span>{Math.floor(frac * 100)}%</span>
        </div>
        {health.consecutiveErrors > 0 && (
          <p className="text-[11.5px] text-wb leading-snug">
            The last {health.consecutiveErrors} request
            {health.consecutiveErrors === 1 ? "" : "s"} to Herrenknecht failed; retrying.
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * Download row for the slurry-only view. Two CSVs because the slurry PC
 * log and the TBM data don't share timestamps; the backend only lets a
 * slurry login fetch these two (slurryMayCall in apps/backend/src/auth.ts).
 */
function SlurryDownload() {
  const tz = useTimezone();
  const pp = usePreprocessingParams();
  const [fromInput, setFromInput] = useState(() =>
    toLocalInputInTz(new Date(Date.now() - 24 * 3600_000), tz),
  );
  const [toInput, setToInput] = useState(() => toLocalInputInTz(new Date(), tz));
  const ready = !!fromInput && !!toInput;
  const fromIso = ready ? localNaiveInputToUtcIso(fromInput, tz) : "";
  const toIso = ready ? localNaiveInputToUtcIso(toInput, tz) : "";
  const slurryHref = ready
    ? api.slurryExportUrl({
        from: fromIso,
        to: toIso,
        channels: Object.keys(SLURRY_CHANNELS) as SlurryChannelId[],
      })
    : undefined;
  const driveHref = ready
    ? api.exportUrl({
        ids: ["chainage", "advanceRate"],
        from: fromIso,
        to: toIso,
        mode: "processed",
        preprocessing: pp,
      })
    : undefined;
  const inputCls = "bg-transparent px-1.5 py-1 text-[11px] text-text focus:outline-none";
  const buttonCls = clsx(
    "px-3 py-1.5 rounded border text-[11px] tracking-wider uppercase transition-colors",
    ready
      ? "bg-surface2 border-border1 text-muted hover:bg-surface3 hover:text-text"
      : "bg-surface2 border-border1 text-dim pointer-events-none opacity-60",
  );
  return (
    <section className="card px-5 py-3 flex flex-wrap items-center gap-3">
      <span className="text-[10px] uppercase tracking-[0.22em] text-dim mr-1">Download</span>
      <div className="inline-flex items-center bg-surface2 border border-border1 rounded px-0.5">
        <input
          type="datetime-local"
          value={fromInput}
          onChange={(e) => setFromInput(e.target.value)}
          className={inputCls}
        />
        <span className="text-dim px-0.5">–</span>
        <input
          type="datetime-local"
          value={toInput}
          onChange={(e) => setToInput(e.target.value)}
          className={inputCls}
        />
      </div>
      <a
        href={slurryHref}
        onClick={() => trackFeature("csv_export_slurry_view", "slurry")}
        className={buttonCls}
      >
        Slurry CSV
      </a>
      <a
        href={driveHref}
        onClick={() => trackFeature("csv_export_drive_view", "slurry")}
        className={buttonCls}
      >
        Chainage &amp; advance CSV
      </a>
    </section>
  );
}

function DesktopApp() {
  const [page, setPage] = useState<PageId>("dashboard");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [resyncOpen, setResyncOpen] = useState(false);
  const [diaryOpen, setDiaryOpen] = useState(false);
  const [pipeScheduleOpen, setPipeScheduleOpen] = useState(false);
  const [alertsOpen, setAlertsOpen] = useState(false);
  // Mobile-only state. Below `md` the sidebar collapses to a slide-in
  // drawer the user opens via a hamburger in the Header. On desktop the
  // sidebar is inline and this state is unused (always false).
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Drive info (including timezone) is shared with the Header via the same
  // queryKey — React Query dedupes.
  const drive = useQuery({ queryKey: ["drive"], queryFn: api.drive, refetchInterval: 10_000 });
  const tz = drive.data?.shiftTimezone ?? "Europe/Dublin";

  // Log a page view whenever the visible page changes (and once on
  // first mount). React-Query / drawer toggles intentionally don't
  // count — only top-level navigations.
  useEffect(() => {
    trackPageView(page);
  }, [page]);

  // Sidebar nav clicks need to close the mobile drawer; on desktop this
  // is a no-op (drawer is never open).
  const navigateTo = (p: PageId) => {
    setPage(p);
    setMobileNavOpen(false);
  };
  const openAndCloseNav = (open: (v: boolean) => void, drawer: string) => () => {
    setMobileNavOpen(false);
    open(true);
    trackFeature(`open_${drawer}`, page);
  };

  return (
    <div className="flex flex-col h-screen bg-bg text-text">
      <Header
        onToggleMobileNav={() => setMobileNavOpen((v) => !v)}
        onOpenAlerts={() => setAlertsOpen(true)}
      />
      <div className="flex flex-1 min-h-0">
        <Sidebar
          page={page}
          onChange={navigateTo}
          onOpenSettings={openAndCloseNav(setSettingsOpen, "preprocessing")}
          onOpenDownload={openAndCloseNav(setDownloadOpen, "download")}
          onOpenResync={openAndCloseNav(setResyncOpen, "resync")}
          onOpenDiary={openAndCloseNav(setDiaryOpen, "diary")}
          onOpenAlerts={openAndCloseNav(setAlertsOpen, "alerts")}
          onOpenPipeSchedule={openAndCloseNav(setPipeScheduleOpen, "pipe_schedule")}
          mobileOpen={mobileNavOpen}
          onMobileClose={() => setMobileNavOpen(false)}
        />
        <main className="flex-1 overflow-y-auto bg-grid">
          {/* Desktop padding is preserved; only the mobile override is added. */}
          <div className="max-w-[1500px] mx-auto px-8 py-7 max-md:px-3 max-md:py-4">
            {page === "dashboard" && <Dashboard timezone={tz} />}
            {page === "drive" && <DriveParameters />}
            {page === "pumps" && <Pumps />}
            {page === "lubrication" && <Lubrication />}
            {page === "jackingForce" && <JackingForce />}
          </div>
        </main>
      </div>
      <PreprocessingDrawer open={settingsOpen} onClose={() => setSettingsOpen(false)} />
      <DownloadDrawer open={downloadOpen} onClose={() => setDownloadOpen(false)} />
      <ResyncDrawer open={resyncOpen} onClose={() => setResyncOpen(false)} />
      <DiaryDrawer open={diaryOpen} onClose={() => setDiaryOpen(false)} />
      <PipeScheduleDrawer open={pipeScheduleOpen} onClose={() => setPipeScheduleOpen(false)} />
      <AlertsDrawer open={alertsOpen} onClose={() => setAlertsOpen(false)} />
    </div>
  );
}
